"use client";

import React, { Fragment, useRef } from "react";
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/lib/hooks";
import { TourCategory } from "@/data/types";
import useClickOutside from "@/hooks/useClickOutside";

interface ServiceItem {
  label: string;
  href: string;
  icon: string;
}

// Per-category icons keyed by tour-category slug. Falls back to a generic
// pin if the slug isn't mapped (so a newly-added category still renders
// gracefully). Slugs match the ones returned by /api/tour-categories.
const CATEGORY_ICONS: Record<string, string> = {
  "private-tour": "👤",
  "group-tour": "👥",
  "street-art-tour": "🎨",
  "bike-tour": "🚴",
  "food-tour": "🍷",
  excursion: "🏞️",
  "car-tour": "🚗",
  "museum-tour": "🏛️",
};

// Name-based fallback — when the slug doesn't match an entry above (because
// the API returned a French slug, a typo, or a brand-new category that
// hasn't been added to the map yet), we pattern-match the category name
// (case-insensitive, French + English keywords) before giving up on the
// final 📌 pin. Order matters: the first regex that hits wins.
const NAME_ICON_RULES: Array<{ test: RegExp; icon: string }> = [
  { test: /vélo|velo|bike|cycl/i, icon: "🚴" },
  { test: /street\s*art|graffiti/i, icon: "🎨" },
  { test: /gastro|food|wine|vin|cuisine|d[ée]gust|culinaire/i, icon: "🍷" },
  { test: /excursion|outdoor|nature|paysage|panoram/i, icon: "🏞️" },
  { test: /mus[ée]e|museum|gallery|galerie|exposition/i, icon: "🏛️" },
  { test: /voiture|car|auto|chauffeur|berline|driv/i, icon: "🚗" },
  { test: /priv[ée]/i, icon: "👤" },
  { test: /groupe|group|team|collectif/i, icon: "👥" },
  { test: /bateau|boat|cruise|crois|ferry|yacht/i, icon: "⛴️" },
  { test: /a[ée]roport|airport|transfert/i, icon: "✈️" },
  { test: /h[ée]lico|helicopter/i, icon: "🚁" },
  { test: /shop|achat|boutique/i, icon: "🛍️" },
  { test: /spa|wellness|bien-?[êe]tre|massage/i, icon: "💆" },
];

const iconForCategory = (slug: string, name: string): string => {
  if (slug && CATEGORY_ICONS[slug]) return CATEGORY_ICONS[slug];
  if (name) {
    for (const rule of NAME_ICON_RULES) {
      if (rule.test.test(name)) return rule.icon;
    }
  }
  return "📌";
};

/**
 * Services megamenu — two columns:
 *   1. SERVICES: hand-picked links to existing routes (transfer, airport
 *      pickup, business trip, cruise, tours hub, quote, gift cards).
 *   2. NOS VISITES: tour categories sourced from Redux
 *      `state.tourCategories.tourCategories`.
 *
 * Every link points at a route that already exists in the app.
 */
const ServicesMegaMenu: React.FC = () => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("guydeez");
  const tourCategories: TourCategory[] = useAppSelector(
    (s) => s.tourCategories.tourCategories
  ) || [];

  const services: ServiceItem[] = [
    {
      label: t("nav_service_airport_pickup", { defaultValue: "Transfert aéroport" }),
      href: "/airport-pickup",
      icon: "✈️",
    },
    {
      label: t("nav_service_transfer", { defaultValue: "Transfert" }),
      href: "/transfer",
      icon: "🚐",
    },
    {
      label: t("nav_service_business_trip", { defaultValue: "Voyage professionnel" }),
      href: "/business-trip",
      icon: "💼",
    },
    {
      label: t("nav_service_cruise", { defaultValue: "Excursions côtières" }),
      href: "/cruise-tour",
      icon: "⛴️",
    },
    {
      label: t("nav_service_our_tours", { defaultValue: "Nos visites" }),
      href: "/tours",
      icon: "📍",
    },
    {
      label: t("nav_service_gift_cards", { defaultValue: "Cartes cadeaux" }),
      href: "/gift-cards",
      icon: "🎁",
    },
  ];

  const handleClose = () => {
    const button = dropdownRef.current?.querySelector(
      'button[data-headlessui-state="open"]'
    ) as HTMLElement | null;
    button?.click();
  };
  useClickOutside(dropdownRef, handleClose);

  return (
    <div ref={dropdownRef} className="relative flex">
      <Popover className="self-center" style={{ display: "contents" }}>
        {({ open, close }) => (
          <>
            <Popover.Button
              className={`group inline-flex items-center text-sm font-medium text-white/90 hover:text-white focus:outline-none px-2 py-2 ${
                open ? "text-white" : ""
              }`}
            >
              <span>{t("nav_services", { defaultValue: "Nos services" })}</span>
              <ChevronDownIcon
                className={`ml-1 h-4 w-4 transition-transform ${open ? "-rotate-180" : ""}`}
                aria-hidden="true"
              />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute left-0 top-full z-40 mt-3 w-[640px]">
                <div className="rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 overflow-hidden">
                  {/* Vertical separator drawn via a right border on the
                      first column — keeps the markup flat (no extra
                      decorative <div>) and stretches to the full height of
                      whichever column is taller. */}
                  <div className="grid grid-cols-2 p-6 gap-x-6">
                    {/* Services */}
                    <div className="pr-6 border-r border-neutral-100">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-3 px-2">
                        {t("nav_services_col", { defaultValue: "Services" })}
                      </div>
                      <ul className="flex flex-col">
                        {services.map((s) => (
                          <li key={s.href}>
                            <Link
                              href={s.href as any}
                              onClick={() => close()}
                              className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-neutral-50"
                            >
                              <span className="text-lg leading-none" aria-hidden>
                                {s.icon}
                              </span>
                              <span className="text-sm font-medium text-neutral-900">
                                {s.label}
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Tour categories */}
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-3 px-2">
                        {t("nav_visites_col", { defaultValue: "Nos visites" })}
                      </div>
                      <ul className="flex flex-col">
                        {tourCategories
                          .filter((c) => c.activated !== false)
                          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                          .map((c) => {
                            const title =
                              c.entry?.data?.[0]?.title ||
                              c.nameTranslated ||
                              c.name;
                            const icon = iconForCategory(c.slug, title);
                            return (
                              <li key={c.uuid}>
                                <Link
                                  href={`/tours?cat=${c.slug}` as any}
                                  onClick={() => close()}
                                  className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-neutral-50"
                                >
                                  <span className="text-lg leading-none" aria-hidden>
                                    {icon}
                                  </span>
                                  <span className="text-sm font-medium text-neutral-900">
                                    {title}
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        {tourCategories.length === 0 && (
                          <li className="px-2 py-2 text-xs text-neutral-500">
                            {t("nav_loading", { defaultValue: "Chargement..." })}
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default ServicesMegaMenu;
